import type { RealtimeEvent } from "@luckymenu/core";
import type { RealtimeStatus } from "@luckymenu/realtime-client";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useActiveRestaurant, useStaffCore, useSupportSession } from "../provider";

/**
 * Keeps the socket open while mounted. Both apps call it once, where a
 * restaurant is ready (signed in, restaurant chosen): it (re)starts on mount
 * and when the restaurant or support session changes, stops on unmount
 * (sign-out, no restaurant), and reconnects at once when the platform says
 * the app woke up or came back online.
 */
export function useLiveConnection(): void {
	const core = useStaffCore();
	const restaurantId = useActiveRestaurant((s) => s.restaurantId);
	const support = useSupportSession((s) => s.session);
	const supportKey = support ? `${support.restaurantId}:${support.token}` : null;
	useEffect(() => {
		const client = core.realtime;
		if (!client) return;
		// The dependencies are the identity of the connection: a change starts over.
		void restaurantId;
		void supportKey;
		client.restart();
		const offWake = core.platform.realtime?.onWake?.(() => client.reconnectNow());
		return () => {
			offWake?.();
			client.stop();
		};
	}, [core, restaurantId, supportKey]);
}

const NO_CONNECTION: RealtimeStatus = {
	state: "idle",
	offline: false,
	role: null,
	lastCloseCode: null,
};
const noop = () => () => {};

/**
 * The connection's status for the offline banner: `offline` is true after 10
 * seconds without a socket. Without a realtime adapter it stays idle.
 */
export function useConnectionStatus(): RealtimeStatus {
	const client = useStaffCore().realtime;
	return useSyncExternalStore(
		client ? (listener) => client.subscribe(listener) : noop,
		() => client?.getStatus() ?? NO_CONNECTION,
		() => client?.getStatus() ?? NO_CONNECTION,
	);
}

/** Calls `listener` for every event while mounted. The latest `listener` is used. */
export function useLiveEvent(listener: (event: RealtimeEvent) => void): void {
	const client = useStaffCore().realtime;
	const latest = useRef(listener);
	latest.current = listener;
	useEffect(() => client?.onEvent((event) => latest.current(event)), [client]);
}
